import type { DivExtensionContext } from '@divkitframework/divkit/typings/common.d.ts';
import type { Variable } from '@divkitframework/divkit/typings/variables.d.ts';

/**
 * Доступ шелла к переменным карточки DivKit.
 *
 * Переменная, объявленная в `card.variables`, перекрывает глобальную с тем же именем: ввод, триггеры и
 * выражения layout работают с card-переменной, а одноимённая глобальная остаётся «мёртвым зеркалом».
 * Публичного API для чтения card-переменных у DivKit web нет, зато его даёт `DivExtensionContext.variables`
 * любого расширения — поэтому `full_height`, стоящий на rootState каждого layout, при монтировании
 * складывает сюда карту переменных. Читать через `readCardVariable`, писать через `setCardVariable`.
 */
const holder: { variables?: Map<string, Variable> } = {};

type Watcher = { name: string; callback: (value: unknown) => void; unsubscribe?: () => void };
const watchers = new Set<Watcher>();

const attach = (watcher: Watcher): void => {
  watcher.unsubscribe?.();
  watcher.unsubscribe = undefined;
  const variable = holder.variables?.get(watcher.name);
  if (!variable) return;
  let first = true;
  watcher.unsubscribe = variable.subscribe((value) => {
    // Svelte-style stores emit the current value on subscribe; only real changes are reported.
    if (first) {
      first = false;
      return;
    }
    watcher.callback(plain(value));
  });
};

/** Reports changes of a card variable (e.g. `currentPage`), surviving layout remounts. */
export const watchCardVariable = (name: string, callback: (value: unknown) => void): (() => void) => {
  const watcher: Watcher = { name, callback };
  watchers.add(watcher);
  attach(watcher);
  return () => {
    watcher.unsubscribe?.();
    watchers.delete(watcher);
  };
};

/**
 * Only a page layout (declares `currentPage`) may take the holder: price cards rendered as separate DivKit
 * instances and modal layouts carry `full_height` too and would otherwise clobber the paywall's variables.
 */
export const captureCardVariables = (context: DivExtensionContext): void => {
  const isPage = context.variables.has('currentPage');
  if (!isPage && holder.variables) return;
  holder.variables = context.variables;
  watchers.forEach(attach);
};

/** Resets only when the unmounting layout is the one captured. */
export const resetCardVariables = (context?: DivExtensionContext): void => {
  if (context && holder.variables && holder.variables !== context.variables) return;
  holder.variables = undefined;
};

export const hasCardVariable = (name: string): boolean => Boolean(holder.variables?.has(name));

/** integer-переменные DivKit хранятся как BigInt — наружу отдаём number (в том числе внутри массивов и словарей). */
const plain = (value: unknown): unknown => {
  if (typeof value === 'bigint') return Number(value);
  if (Array.isArray(value)) return value.map(plain);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, plain(item)]),
    );
  }
  return value;
};

export const readCardVariable = (name: string): unknown => plain(holder.variables?.get(name)?.getValue());

export const setCardVariable = (name: string, value: unknown): boolean => {
  const variable = holder.variables?.get(name);
  if (!variable) return false;
  if (typeof value === 'string') variable.set(value);
  else variable.setValue(value as never);
  return true;
};

export const listCardVariables = (): Array<[string, unknown]> =>
  holder.variables ? [...holder.variables.entries()].map(([name, variable]) => [name, plain(variable.getValue())]) : [];

/**
 * JSON-совместимый снимок всех card-переменных, кроме `exclude` — для сохранения состояния квиза. Переменные,
 * которые layout меняет сам (ввод через `text_variable`, `variable_triggers`), в stat-событиях не видны, поэтому
 * восстановить их после перезагрузки можно только из такого снимка.
 */
export const snapshotCardVariables = (exclude: ReadonlySet<string>): Record<string, unknown> | undefined => {
  if (!holder.variables) return undefined;
  const snapshot: Record<string, unknown> = {};
  for (const [name, variable] of holder.variables) {
    if (exclude.has(name)) continue;
    snapshot[name] = plain(variable.getValue());
  }
  return snapshot;
};
